import * as React from 'react';
import { useCallback, useId } from 'react';
import {
  Stack,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonGroupProps,
  Tooltip,
  Typography,
  TypographyVariant,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { QuestionIcon } from '@shared/custom-icons';

type StringOrNumberOrNull = string | number | null;

export type ToggleGroupVariant = 'default' | 'pill';

export const toggleGroupSx = (theme: Theme) => {
  const { shape, spacing } = theme;
  const palette = theme.vars.palette;
  const primaryAlpha = (opacity: number) =>
    theme.vars
      ? `rgba(${theme.vars.palette.primary.mainChannel} / ${opacity})`
      : alpha(theme.palette.primary.main, opacity);
  return {
    gap: spacing(0.5),
    borderRadius: `${Number(shape.borderRadius) * 2}px`,
    backgroundColor: primaryAlpha(0.06),
    '& .MuiToggleButtonGroup-grouped': {
      margin: 0,
      border: 'none !important',
      borderRadius: `${Number(shape.borderRadius) * 1.5}px !important`,
      fontWeight: 600,
      color: palette.text.secondary,
      background: 'transparent',
      '&:hover, &:focus': {
        boxShadow: 'none',
        color: palette.text.primary,
        background: primaryAlpha(0.1),
      },
      '&.Mui-selected': {
        '&, &.MuiToggleButton-primary, &.MuiToggleButton-standard': {
          color: palette.common.white,
          background: palette.primary.main,
        },
        '&:hover': {
          boxShadow: 'none',
          color: palette.text.primary,
          background: palette.primary.light,
        },
      },
    },
    ...theme.applyStyles('dark', {
      '& .MuiToggleButtonGroup-grouped.Mui-selected:hover': {
        color: palette.background.default,
      },
    }),
  };
};

export interface ToggleGroupOption<T extends StringOrNumberOrNull> {
  label: string;
  value: T;
}

interface Props<T extends StringOrNumberOrNull> {
  label?: string;
  labelDirection?: 'horizontal' | 'vertical';
  options: ToggleGroupOption<Exclude<T, null>>[];
  value: T;
  onChange: (val: T) => void;
  size?: ToggleButtonGroupProps['size'];
  nullable?: boolean;
  justifyContent?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  description?: string;
  labelVariant?: TypographyVariant;
  variant?: ToggleGroupVariant;
}

export function ToggleGroup<T extends StringOrNumberOrNull>({
  label,
  labelDirection = 'horizontal',
  options,
  onChange,
  value,
  size,
  nullable = false,
  justifyContent = 'inherit',
  fullWidth = false,
  disabled,
  description,
  labelVariant,
  variant = 'default',
}: Props<T>) {
  const groupId = useId();
  const handleOnChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, val: T) => {
      if (val || nullable) onChange(val);
    },
    [nullable, onChange]
  );

  return (
    <Stack
      gap={labelDirection === 'horizontal' ? 1 : 0}
      flexDirection={labelDirection === 'horizontal' ? 'row' : 'column'}
      alignItems={labelDirection === 'horizontal' ? 'center' : 'flex-start'}
      justifyContent={justifyContent}
    >
      {label && (
        <Stack direction="row" gap={1} alignItems="center">
          <Typography
            component="span"
            id={`${groupId}-label`}
            variant={labelVariant ? labelVariant : 'subtitle2'}
            color="text.secondary"
            sx={{
              width: 'fit-content',
              maxWidth: '100%',
            }}
          >
            {label}
          </Typography>
          {description && (
            <Tooltip title={description}>
              <QuestionIcon
                sx={{ width: 20, height: 20, color: 'text.secondary' }}
                aria-label={description}
              />
            </Tooltip>
          )}
        </Stack>
      )}
      <ToggleButtonGroup
        id={groupId}
        aria-labelledby={label ? `${groupId}-label` : undefined}
        size={size}
        disabled={disabled}
        exclusive
        value={value}
        onChange={handleOnChange}
        fullWidth={fullWidth}
        sx={variant === 'pill' ? toggleGroupSx : undefined}
      >
        {options.map(({ label, value }) => (
          <ToggleButton value={value} key={value}>
            {label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );
}
